import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { Leaf, Plus, Download, Calendar, ArrowLeft, LayoutGrid, List, Trash2, CheckCircle, AlertTriangle, CheckSquare, Square, X, RotateCcw, Sparkles } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { nativeDownload } from '../utils/nativeDownload';
import SignatureManager from '../components/profile/SignatureManager';

export default function PflanzeDerWocheList() {
    const [plants, setPlants] = useState([]);
    const [trashPlants, setTrashPlants] = useState([]);
    const [trainingWeeks, setTrainingWeeks] = useState([]);
    const [selectedWeekFilter, setSelectedWeekFilter] = useState('all');
    const [currentTab, setCurrentTab] = useState('active'); // active | trash
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('pdw_view_mode') || 'list');
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDownloading, setIsBatchDownloading] = useState(false);
    const [showOnlyComplete, setShowOnlyComplete] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const navigate = useNavigate();
    const { fetchWithAuth, authToken } = useContext(AuthContext);
    const { showToast: addToast } = useToast();
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, id: null });
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        if (currentTab === 'active') {
            fetchPlants();
            fetchTrainingWeeks();
            fetchProfile();
        } else {
            fetchTrash();
        }
    }, [currentTab]);

    const fetchProfile = async () => {
        try {
            const response = await fetchWithAuth('/api/profile/');
            if (response.ok) {
                const data = await response.json();
                setHasSignature(data.has_signature);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
    };

    const fetchTrainingWeeks = async () => {
        try {
            const response = await fetchWithAuth('/api/apprentice/weeks/');
            if (response.ok) {
                const data = await response.json();
                setTrainingWeeks(data);
            }
        } catch (error) {
            console.error('Error fetching weeks:', error);
        }
    };

    const fetchPlants = async () => {
        try {
            console.log('[PflanzeDerWoche] Fetching active plants...');
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/');
            
            // Log debug headers from my latest backend fix
            console.log('[PflanzeDerWoche] API Status:', response.status);
            console.log('[PflanzeDerWoche] X-PDW-Count:', response.headers.get('X-PDW-Count'));
            console.log('[PflanzeDerWoche] X-User-Staff:', response.headers.get('X-User-Staff'));
            
            if (response.ok) {
                const data = await response.json();
                console.log(`[PflanzeDerWoche] Received ${data.length} plants`);
                setPlants(data);
            } else {
                const errData = await response.json().catch(() => ({}));
                console.error('[PflanzeDerWoche] Error loading plants:', response.status, errData);
                addToast(`Fehler beim Laden (${response.status})`, 'error');
            }
        } catch (error) {
            console.error('Error fetching plants of the week:', error);
            addToast('Fehler beim Laden der Pflanzen der Woche.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchTrash = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/trash/');
            if (response.ok) {
                const data = await response.json();
                setTrashPlants(data);
            }
        } catch (error) {
            console.error('Error fetching trash:', error);
            addToast('Fehler beim Laden des Papierkorbs.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleView = (mode) => {
        setViewMode(mode);
        localStorage.setItem('pdw_view_mode', mode);
    };

    const toggleSelect = (e, id) => {
        e.stopPropagation();
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === plants.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(plants.map(p => p.id));
        }
    };

    const handleDelete = (e, id) => {
        if (e && e.stopPropagation) e.stopPropagation();
        setConfirmModal({
            show: true,
            title: 'Eintrag löschen',
            message: 'Möchtest du diesen Eintrag wirklich in den Papierkorb verschieben?',
            id: id,
            onConfirm: async (idToDelete) => {
                console.log(`[PflanzeDerWoche] DELETING id=${idToDelete}`);
                try {
                    const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${idToDelete}/`, {
                        method: 'DELETE'
                    });
                    if (response.ok) {
                        setPlants(prev => {
                            const filtered = prev.filter(p => p.id !== idToDelete);
                            console.log(`[PflanzeDerWoche] State updated. Old count: ${prev.length}, New count: ${filtered.length}`);
                            return filtered;
                        });
                        setSelectedIds(prev => prev.filter(i => i !== idToDelete));
                        addToast('Eintrag in den Papierkorb verschoben', 'success');
                    } else {
                        addToast(`Fehler beim Löschen (${response.status})`, 'error');
                    }
                } catch (error) {
                    console.error('[PflanzeDerWoche] Delete exception:', error);
                    addToast('Netzwerkfehler beim Löschen', 'error');
                }
            }
        });
    };

    const handleRestore = async (e, id) => {
        e.stopPropagation();
        try {
            const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${id}/restore/`, {
                method: 'POST'
            });
            if (response.ok) {
                setTrashPlants(prev => prev.filter(p => p.id !== id));
                addToast('Eintrag wiederhergestellt', 'success');
            } else {
                addToast('Fehler beim Wiederherstellen', 'error');
            }
        } catch (error) {
            console.error('Error restoring:', error);
            addToast('Fehler beim Wiederherstellen', 'error');
        }
    };

    const handleForceDelete = (e, id) => {
        if (e && e.stopPropagation) e.stopPropagation();
        setConfirmModal({
            show: true,
            title: 'Endgültig löschen',
            message: 'Möchtest du diesen Eintrag wirklich UNWIDERRUFLICH löschen? Dies kann nicht rückgängig gemacht werden.',
            id: id,
            onConfirm: async (idToDelete) => {
                try {
                    const response = await fetchWithAuth(`/api/apprentice/pflanze-der-woche/${idToDelete}/force_delete/`, {
                        method: 'DELETE'
                    });
                    if (response.ok) {
                        setTrashPlants(prev => prev.filter(p => p.id !== idToDelete));
                        addToast('Eintrag dauerhaft gelöscht', 'success');
                    } else {
                        addToast(`Fehler beim Löschen (${response.status})`, 'error');
                    }
                } catch (error) {
                    console.error('Error force deleting:', error);
                    addToast('Netzwerkfehler beim Löschen', 'error');
                }
            }
        });
    };

    const handleDownloadPdf = async (e, id, plantName) => {
        e.stopPropagation();
        await nativeDownload(
            `/api/apprentice/pflanze-der-woche/${id}/download_pdf/`,
            authToken,
            (msg) => addToast(msg, 'error')
        );
    };

    const handleBatchDownloadZip = async () => {
        if (selectedIds.length === 0) return;
        setIsBatchDownloading(true);
        try {
            await nativeDownload(
                `/api/apprentice/pflanze-der-woche/batch_download_zip/?ids=${selectedIds.join(',')}`,
                authToken,
                (msg) => addToast(msg, 'error')
            );
            addToast(`${selectedIds.length} PDFs werden als ZIP heruntergeladen`, 'success');
            setSelectedIds([]);
        } finally {
            setIsBatchDownloading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setConfirmModal({
            show: true,
            title: 'Auswahl löschen',
            message: `Möchtest du wirklich ${selectedIds.length} Einträge in den Papierkorb verschieben?`,
            onConfirm: async () => {
                try {
                    const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/bulk_delete/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ids: selectedIds })
                    });
                    if (response.ok) {
                        setPlants(prev => prev.filter(p => !selectedIds.includes(p.id)));
                        setSelectedIds([]);
                        addToast(`${selectedIds.length} Einträge gelöscht`, 'success');
                    }
                } catch (error) {
                    addToast('Fehler beim Massen-Löschen', 'error');
                }
            }
        });
    };

    const handleBulkRestore = async () => {
        if (selectedIds.length === 0) return;
        try {
            const response = await fetchWithAuth('/api/apprentice/pflanze-der-woche/bulk_restore/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: selectedIds })
            });
            if (response.ok) {
                setTrashPlants(prev => prev.filter(p => !selectedIds.includes(p.id)));
                setSelectedIds([]);
                addToast(`${selectedIds.length} Einträge wiederhergestellt`, 'success');
                setCurrentTab('active');
            }
        } catch (error) {
            addToast('Fehler bei der Wiederherstellung', 'error');
        }
    };

    const activeDataToGroup = currentTab === 'active' ? plants : trashPlants;
    const filteredPlants = activeDataToGroup.filter(p => {
        if (showOnlyComplete && p.validation_error_count > 0) return false;
        
        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesName = (p.plant_name || '').toLowerCase().includes(query);
            const matchesGerman = (p.plant_german_name || '').toLowerCase().includes(query);
            if (!matchesName && !matchesGerman) return false;
        }

        if (selectedWeekFilter === 'all') return true;
        if (selectedWeekFilter === 'unassigned') return !p.training_week;
        return p.training_week?.toString() === selectedWeekFilter;
    });

    // Group plants by week
    const groupedPlants = filteredPlants.reduce((acc, plant) => {
        const key = plant.training_week_info ? plant.training_week_info.display : 'Nicht zugeordnet';
        if (!acc[key]) acc[key] = [];
        acc[key].push(plant);
        return acc;
    }, {});

    // Sort groups: Let's extract the ones with "KW" first (sort descending by string), and "Nicht zugeordnet" at the end
    const sortedGroupKeys = Object.keys(groupedPlants).sort((a, b) => {
        if (a === 'Nicht zugeordnet') return 1;
        if (b === 'Nicht zugeordnet') return -1;
        // Basic reverse sort so newer weeks are generally at the top
        return b.localeCompare(a);
    });

    const PlantCard = ({ entry, isSelected, onSelect, onDelete, onRestore, onForceDelete, isTrash, onClick }) => {
        const hasWorkRemaining = entry.validation_error_count > 0;

        return (
            <div
                onClick={onClick}
                className={`group relative overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-2xl border-2 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-xl ${
                    isSelected 
                    ? 'border-plantlix-500 ring-4 ring-plantlix-500/10' 
                    : 'border-gray-100 dark:border-gray-800'
                }`}
            >
                {/* Visual Accent */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${hasWorkRemaining ? 'from-orange-400 to-yellow-400' : 'from-plantlix-500 to-plantlix-400'}`} />

                {/* Selection Overlay */}
                <button
                    onClick={(e) => { e.stopPropagation(); onSelect(e); }}
                    className={`absolute top-4 left-4 z-10 p-1.5 rounded-lg transition-all ${
                        isSelected 
                        ? 'bg-plantlix-500 text-white' 
                        : 'bg-white/90 dark:bg-black/40 text-gray-400 hover:text-plantlix-500 opacity-0 group-hover:opacity-100'
                    }`}
                >
                    {isSelected ? <CheckCircle className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                </button>

                <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-2xl">
                            <Leaf className={`w-6 h-6 ${hasWorkRemaining ? 'text-orange-400' : 'text-plantlix-500'}`} />
                        </div>
                        
                        {!isTrash ? (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(e); }}
                                className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        ) : (
                            <div className="flex gap-2">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRestore(e); }}
                                    className="p-2 text-plantlix-500 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-xl"
                                >
                                    <RotateCcw className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onForceDelete(e); }}
                                    className="p-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-xl"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>

                    <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1 line-clamp-1 leading-tight">
                        {entry.plant_name || "Unbekannt"}
                    </h3>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-6 uppercase tracking-wider">
                        {entry.plant_german_name || "-"}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-6">
                        {entry.training_week_info && (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase tracking-tighter border border-blue-100 dark:border-blue-800">
                                <Calendar className="w-3 h-3" />
                                {entry.training_week_info.display}
                            </span>
                        )}
                        
                        {hasWorkRemaining ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-orange-50 dark:bg-orange-900/20 text-orange-500 rounded-full text-[10px] font-black uppercase tracking-tighter border border-orange-100 dark:border-orange-800">
                                <AlertTriangle className="w-3 h-3" />
                                Unvollständig
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-500 rounded-full text-[10px] font-black uppercase tracking-tighter border border-plantlix-100 dark:border-plantlix-800">
                                <CheckCircle className="w-3 h-3" />
                                Vollständig
                            </span>
                        )}
                    </div>

                    <div className="pt-5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                         <div className="flex flex-col">
                            <span className="text-[9px] font-black text-gray-300 dark:text-gray-600 uppercase tracking-widest">
                                ID: {entry.id.toString().padStart(4, '0')}
                            </span>
                        </div>
                        
                        <div className="flex gap-2">
                             <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/apprentice/pflanze-der-woche/${entry.id}`); }}
                                className="px-4 py-2 bg-gray-900 dark:bg-black hover:bg-plantlix-500 text-white rounded-xl text-xs font-bold transition-all shadow-md"
                            >
                                Edit
                            </button>
                            <button
                                onClick={(e) => handleDownloadPdf(e, entry.id, entry.plant_name || 'Unbekannt')}
                                className="p-2 bg-plantlix-500/10 hover:bg-plantlix-500 text-plantlix-500 hover:text-white rounded-xl transition-all"
                                title="PDF Download"
                            >
                                <Download className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const PlantListItem = ({ entry, isSelected, onSelect, onDelete, onRestore, onForceDelete, isTrash, onClick }) => {
        const hasWorkRemaining = entry.validation_error_count > 0;
        const fd = entry.form_data || {};
        
        // Define limits directly here for validation badge
        const limits = {
            botanical_name: 80, german_name: 80, family: 80, growth_form: 80,
            origin: 80, distribution: 80, observed_location: 80, height: 30, width: 30,
            leaf_info: 200, autumn_color: 80, life_zone: 80, flower_color: 80,
            flower_inflorescence: 200, fruit_info: 200, care_notes: 200, artname_erklärung: 200
        };

        const overLimit = Object.entries(fd).some(([key, val]) => {
            if (typeof val !== 'string') return false;
            const limit = limits[key] || 200;
            return val.length > limit;
        });

        return (
            <div
                onClick={onClick}
                className={`group bg-white dark:bg-[#1a1a1a] py-1.5 px-3 shadow-sm hover:shadow-md transition-all cursor-pointer border-l-4 ${
                    isSelected ? 'border-plantlix-500 bg-plantlix-50/10' : 'border-gray-200 dark:border-gray-800'
                } flex items-center justify-between rounded-r-md mb-1`}
            >
                <div className="flex items-center gap-3 overflow-hidden">
                    <button
                        onClick={(e) => { e.stopPropagation(); onSelect(e); }}
                        className={`p-0.5 rounded transition-colors ${isSelected ? 'text-plantlix-500' : 'text-gray-300'}`}
                    >
                        {isSelected ? <CheckCircle className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>

                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 dark:text-white text-sm truncate">
                                {entry.plant_name || "Unbekannt"}
                            </h3>
                            {hasWorkRemaining && <AlertTriangle className="w-3 h-3 text-orange-500" title="Fehlende Daten" />}
                            {overLimit && <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Limit überschritten!" />}
                        </div>
                        <div className="flex gap-2 text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                            {entry.plant_german_name && <span className="italic truncate max-w-[120px]">{entry.plant_german_name}</span>}
                            <span>{new Date(entry.created_at).toLocaleDateString('de-DE')}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {entry.training_week_info && (
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-100/50 dark:bg-blue-900/40 px-1.5 py-0.5 rounded">
                            {entry.training_week_info.display}
                        </span>
                    )}
                    
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/apprentice/pflanze-der-woche/${entry.id}`); }}
                            className="p-1 text-gray-400 hover:text-plantlix-500"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                        <button
                            onClick={(e) => handleDownloadPdf(e, entry.id, entry.plant_name || 'Unbekannt')}
                            className="p-1 text-gray-400 hover:text-blue-500"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4 pb-24">
            <div className="max-w-6xl mx-auto relative">
                {/* Header */}
                <button
                    onClick={() => navigate('/apprentice-journal')}
                    className="mb-4 flex items-center gap-2 text-gray-600 hover:text-plantlix-500 dark:text-gray-400 dark:hover:text-plantlix-400 font-medium transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Zurück zum Ausbildungsnachweis
                </button>

                <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="animate-in slide-in-from-left duration-700">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-100 dark:border-gray-800">
                                <Leaf className="w-8 h-8 text-plantlix-500 dark:text-plantlix-400" />
                            </div>
                            <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
                                PdW
                            </h1>
                        </div>
                        <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                            Pflanze der Woche <span className="text-plantlix-500 opacity-50 px-1">•</span> <span className="italic">Botanische Dokumentation</span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        {plants.length > 0 && (
                            <div className="hidden md:flex items-center gap-2 bg-white dark:bg-[#1a1a1a] px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                <span className="text-xs font-bold text-gray-500 uppercase">Filter:</span>
                                <select
                                    value={selectedWeekFilter}
                                    onChange={(e) => setSelectedWeekFilter(e.target.value)}
                                    className="text-sm bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 font-medium cursor-pointer"
                                >
                                    <option value="all">Alle Wochen</option>
                                    <option value="unassigned">Nicht zugeordnet</option>
                                    {trainingWeeks.map(week => (
                                        <option key={week.id} value={week.id}>
                                            KW {week.week_number}/{week.year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button
                            onClick={() => setShowOnlyComplete(!showOnlyComplete)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-sm font-medium ${showOnlyComplete ? 'bg-plantlix-100 border-plantlix-300 text-plantlix-700 dark:bg-plantlix-900/30 dark:border-plantlix-800 dark:text-plantlix-400' : 'bg-white border-gray-200 text-gray-600 dark:bg-[#1a1a1a] dark:border-gray-700 dark:text-gray-400 hover:bg-gray-50'}`}
                            title="Nur vollständig ausgefüllte Pflanzen anzeigen"
                        >
                            <CheckCircle className={`w-4 h-4 ${showOnlyComplete ? 'text-plantlix-600' : 'text-gray-400'}`} />
                            <span>Nur Vollständige</span>
                        </button>

                        {plants.length > 0 && (
                            <button
                                onClick={toggleSelectAll}
                                className="text-sm font-medium text-gray-500 hover:text-plantlix-500 dark:text-gray-400 transition-colors flex items-center gap-2"
                            >
                                {selectedIds.length === plants.length ? <X className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
                                {selectedIds.length === plants.length ? 'Auswahl aufheben' : 'Alle auswählen'}
                            </button>
                        )}

                        <div className="flex bg-white dark:bg-[#1a1a1a] p-1 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => handleToggleView('grid')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700 text-plantlix-500' : 'text-gray-400 hover:text-gray-600'}`}
                                title="Grid-Ansicht"
                            >
                                <LayoutGrid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => handleToggleView('list')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700 text-plantlix-500' : 'text-gray-400 hover:text-gray-600'}`}
                                title="Listen-Ansicht"
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <p className="text-gray-600 dark:text-gray-400">
                        Erstelle und manage deine "Pflanze der Woche" PDFs.
                    </p>
                    
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Leaf className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Suchen..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-[#1a1a1a] text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-plantlix-500 focus:border-transparent transition-all"
                        />
                    </div>
                </div>

                {/* TOP HEADER SECTION */}
                <div className="flex flex-col lg:flex-row gap-6 mb-8">
                    {/* Left: Info & Action */}
                    <div className="flex-1">
                        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-premium p-6 h-full flex flex-col justify-between">
                            <div>
                                <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Pflanzendokumentation</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                                    Erstelle und verwalte deine botanischen Belege. Nutze die automatische Befüllung für maximale Effizienz.
                                </p>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <button
                                    onClick={() => navigate('/apprentice/pflanze-der-woche/new')}
                                    className="flex items-center justify-center gap-2 px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 active:scale-95 text-white rounded-xl font-bold transition-all shadow-lg shadow-plantlix-500/20 w-full sm:w-auto"
                                >
                                    <Plus className="w-5 h-5" />
                                    <span>Pflanze hinzufügen</span>
                                </button>
                                
                                <div className="hidden sm:flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest px-4">
                                    <Sparkles className="w-3 h-3 text-plantlix-500" />
                                    <span>AI Auto-Fill bereit</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Signature (Compact) */}
                    <div className="lg:w-80">
                        <SignatureManager
                            hasSignature={hasSignature}
                            onUpdate={() => fetchProfile()}
                        />
                    </div>
                </div>

                {/* Batch Action Bar (Sticky at bottom) */}
                {selectedIds.length > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="bg-gray-900 dark:bg-black text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 border border-white/10 backdrop-blur-md">
                            <div className="flex items-center gap-2 border-r border-white/20 pr-6">
                                <span className="text-plantlix-400 font-bold text-lg">{selectedIds.length}</span>
                                <span className="text-sm text-gray-300">Pflanzen ausgewählt</span>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleBatchDownloadZip}
                                    disabled={isBatchDownloading}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-xl font-semibold transition-all ${isBatchDownloading ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-plantlix-500 hover:bg-plantlix-600 text-white shadow-lg'}`}
                                >
                                    {isBatchDownloading ? <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-white rounded-full"></div> : <Download className="w-4 h-4" />}
                                    <span>ZIP Download</span>
                                </button>

                                {currentTab === 'active' ? (
                                    <button
                                        onClick={handleBulkDelete}
                                        className="flex items-center gap-2 px-6 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-xl font-semibold transition-all border border-red-500/20"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span>Löschen</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleBulkRestore}
                                        className="flex items-center gap-2 px-6 py-2 bg-plantlix-500/10 hover:bg-plantlix-500/20 text-plantlix-500 rounded-xl font-semibold transition-all border border-plantlix-500/20"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        <span>Wiederherstellen</span>
                                    </button>
                                )}

                                <button
                                    onClick={() => setSelectedIds([])}
                                    className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                                    title="Auswahl aufheben"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex space-x-1 mb-6 bg-gray-100/50 dark:bg-gray-800/50 p-1 rounded-xl">
                    <button
                        onClick={() => setCurrentTab('active')}
                        className={`flex-1 flex items-center justify-center py-2.5 px-4 rounded-lg font-semibold transition-all ${
                            currentTab === 'active' 
                            ? 'bg-white dark:bg-gray-700 text-plantlix-500 dark:text-plantlix-400 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600' 
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        Aktive Pflanzen
                    </button>
                    <button
                        onClick={() => setCurrentTab('trash')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-semibold transition-all ${
                            currentTab === 'trash' 
                            ? 'bg-white dark:bg-gray-700 text-red-500 dark:text-red-400 shadow-sm ring-1 ring-gray-200 dark:ring-gray-600' 
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                        <Trash2 className="w-4 h-4" />
                        Papierkorb
                    </button>
                </div>
                
                {/* Info Box if Trash is empty/full */}
                {currentTab === 'trash' && trashPlants.length > 0 && (
                    <div className="flex justify-between items-center mb-6 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 p-4 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm font-medium">Einträge im Papierkorb werden (noch) nicht automatisch gelöscht. Du kannst sie wiederherstellen oder dauerhaft vernichten.</p>
                        </div>
                    </div>
                )}

                {/* List View */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-plantlix-500"></div>
                        <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Einträge...</p>
                    </div>
                ) : activeDataToGroup.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700">
                        {currentTab === 'active' ? (
                            <>
                                <Leaf className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    Noch keine "Pflanze der Woche"
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Beginne jetzt mit deiner Datensammlung.
                                </p>
                                <button
                                    onClick={() => navigate('/apprentice/pflanze-der-woche/new')}
                                    className="px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-lg font-semibold transition-all"
                                >
                                    Erste Pflanze anlegen
                                </button>
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    Papierkorb ist leer
                                </h3>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="space-y-12">
                        {sortedGroupKeys.map(groupKey => (
                            <div key={groupKey}>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                                    <Calendar className="w-5 h-5 text-plantlix-500" />
                                    {groupKey}
                                    <span className="text-sm font-normal text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full ml-2">
                                        {groupedPlants[groupKey].length} {groupedPlants[groupKey].length === 1 ? 'Eintrag' : 'Einträge'}
                                    </span>
                                </h3>

                                {viewMode === 'grid' ? (
                                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                                        {groupedPlants[groupKey].map(entry => (
                                            <PlantCard 
                                                key={entry.id} 
                                                entry={entry} 
                                                onClick={() => navigate(`/apprentice/pflanze-der-woche/${entry.id}`)}
                                                isSelected={selectedIds.includes(entry.id)}
                                                onSelect={(e) => toggleSelect(e, entry.id)}
                                                onDelete={(e) => handleDelete(e, entry.id)}
                                                onRestore={(e) => handleRestore(e, entry.id)}
                                                onForceDelete={(e) => handleForceDelete(e, entry.id)}
                                                isTrash={currentTab === 'trash'}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {groupedPlants[groupKey].map(entry => (
                                            <PlantListItem 
                                                key={entry.id} 
                                                entry={entry} 
                                                onClick={() => navigate(`/apprentice/pflanze-der-woche/${entry.id}`)}
                                                isSelected={selectedIds.includes(entry.id)}
                                                onSelect={(e) => toggleSelect(e, entry.id)}
                                                onDelete={(e) => handleDelete(e, entry.id)}
                                                onRestore={(e) => handleRestore(e, entry.id)}
                                                onForceDelete={(e) => handleForceDelete(e, entry.id)}
                                                isTrash={currentTab === 'trash'}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {/* Custom Confirmation Modal */}
                {confirmModal.show && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 dark:border-gray-700 animate-in zoom-in-95 duration-200">
                            <div className="p-6">
                                <div className="flex items-center gap-3 mb-4 text-red-500">
                                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-full">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-bold dark:text-white">{confirmModal.title}</h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                    {confirmModal.message}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                                        className="px-5 py-2.5 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        onClick={() => {
                                            confirmModal.onConfirm(confirmModal.id);
                                            setConfirmModal({ ...confirmModal, show: false });
                                        }}
                                        className="px-5 py-2.5 rounded-xl font-semibold bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 transition-all"
                                    >
                                        Löschen bestätigen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
